/* eslint-disable jsx-a11y/alt-text */
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import EditCustomerModal from "src/components/modals/EditCustomerModal";
import BackIcon from "../images/back.png";
import "lineicons/dist/lineicons.css";
import { cn, getCustomizationValue, renderCurrency, showSuccessToast } from "src/lib/utils";
import CustomModal from "src/components/CustomModal";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { archiveCustomer, generateVirtualAccount, getCustomerDetails, unArchiveCustomer } from "src/api/customer";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { useAuth } from "src/providers/AuthProvider";
import AddSubAccount from "src/components/modals/AddSubAccount";
import CustomDataTable from "src/components/CustomDataTable";
import CustomDropdown, { dropdownActionVariants } from "src/components/CustomDropdown";
import { Button } from "src/components/ui/button";
import { EllipsisIcon } from "src/svg";
import { deleteSubAccount } from "src/api/subAccount";

const CustomerDetail = () => {
	const { user } = useAuth();
	const { uuid } = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const [{ data }] = useQueries({
		queries: [
			{
				queryKey: ["customers", uuid],
				queryFn: () => getCustomerDetails(uuid),
				suspense: true,
			},
		],
	});
	const { mutate } = useMutation(generateVirtualAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["customers"]);
		},
	});

	const customer = data?.data?.contact_details;
	const customerData = data?.data;

	const { mutate: archiveMutate, isLoading } = useMutation(customer?.is_archived ? unArchiveCustomer : archiveCustomer, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["customers"]);
		},
	});
	const { mutate: deleteSubAccountMutate } = useMutation(deleteSubAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message);
			queryClient.invalidateQueries(["customers"]);
		},
	});

	const columns = [
		{ name: "First Name", selector: (row) => row.firstname },
		{ name: "Last Name", selector: (row) => row.lastname },
		{ name: "Id Number", selector: (row) => row.idNo ?? "-" },
		{
			name: "Actions",
			cell: (row) => (
				<CustomDropdown
					trigger={
						<Button variant="hover">
							<EllipsisIcon />
						</Button>
					}
					actions={[
						user?.organization_type_name === "Schools" && {
							label: (
								<CustomModal
									title={cn("Edit ", getCustomizationValue(user, "sub_account_label", true))}
									trigger={<span>Edit {getCustomizationValue(user, "sub_account_label", true)}</span>}
								>
									<AddSubAccount customer_id={customer?.id} subAccount={row} />
								</CustomModal>
							),
							asChild: true,
						},
						{
							label: (
								<CustomConfirmDialog
									trigger={
										<span className={dropdownActionVariants({ variant: "destructive" })}>
											Delete {getCustomizationValue(user, "sub_account_label", true)}
										</span>
									}
									mutateFn={() => deleteSubAccountMutate({ uuid: row.uuid })}
								/>
							),
							asChild: true,
						},
					]}
				/>
			),
			grow: 0.5,
		},
	];

	if (!customer) {
		return (
			<div className="pageWrapper">
				<div className="pageTop">
					<h2>{getCustomizationValue(user, "customer_label", true)} details</h2>
				</div>

				<div className="innerContent">
					<p>{getCustomizationValue(user, "customer_label", true)} not found</p>
				</div>
			</div>
		);
	}

	return (
		<div className="pageWrapper">
			<div className="pageTop">
				<button className="transparent-btn" onClick={() => navigate("..", { relative: "path" })}>
					<img src={BackIcon} />
				</button>
				<h2>{getCustomizationValue(user, "customer_label", true)} details</h2>
			</div>

			<div className="innerContent">
				<div className="mb-2 inner-top tw-flex">
					<div className="inner-top-right tw-gap-4 tw-ml-auto">
						{user?.organization_type_name === "Schools" && (
							<CustomModal
								title={cn("Add ", getCustomizationValue(user, "sub_account_label", true))}
								trigger={<button className="blue-btn">Add {getCustomizationValue(user, "sub_account_label", true)}</button>}
							>
								<AddSubAccount customer_id={customer?.id} />
							</CustomModal>
						)}
						<CustomConfirmDialog
							trigger={<button className="green-btn">Generate Account Statement</button>}
							mutateFn={() => mutate({ uuid: customer?.uuid, bvn: customer?.bvn })}
						/>
					</div>
				</div>

				<div className="mb-3 inner-btm">
					<div className="contact-details tw-gap-6 !tw-grid">
						<div className="section-title tw-flex-wrap-reverse tw-gap-4">
							<h3>Contact Details</h3>

							<div className="tw-flex tw-gap-4 tw-flex-wrap">
								<CustomModal
									title={cn("Edit ", getCustomizationValue(user, "customer_label", true))}
									trigger={<button className="blue-btn">Edit {getCustomizationValue(user, "customer_label", true)}</button>}
								>
									<EditCustomerModal customer={customer} />
								</CustomModal>
								<CustomConfirmDialog
									trigger={
										<button disabled={isLoading} className="orange-btn">
											{customer?.is_archived ? "Un Archive" : "Archive"} {getCustomizationValue(user, "customer_label", true)}
										</button>
									}
									mutateFn={() => archiveMutate({ uuid: customer.uuid })}
								/>
							</div>
						</div>

						<div className="tw-grid tw-gap-5 tw-grid-cols-[repeat(auto-fit,minmax(250px,1fr))]">
							<div className="contact-detail-bloc">
								<div className="label">{getCustomizationValue(user, "customer_label", true)} Name</div>
								<div className="txt-cont">
									<h3>
										{customer?.first_name} {customer?.last_name}
									</h3>
								</div>
							</div>

							<div className="contact-detail-bloc">
								<div className="label">{getCustomizationValue(user, "customer_label", true)} ID:</div>
								<div className="txt-cont">
									<h3>{customer?.customer_id}</h3>
								</div>
							</div>

							<div className="contact-detail-bloc">
								<div className="label">{getCustomizationValue(user, "customer_label", true)} Email:</div>
								<div className="txt-cont">
									<h3>{customer?.email}</h3>
								</div>
							</div>

							<div className="contact-detail-bloc">
								<div className="label">{getCustomizationValue(user, "customer_label", true)} Phone:</div>
								<div className="txt-cont">
									<h3>{customer?.phone}</h3>
								</div>
							</div>

							{/* <div className="contact-detail-bloc">
								<div className="label">BVN:</div>
								<div className="txt-cont">
									<h3>{customer?.bvn}</h3>
								</div>
							</div> */}

							<div className="contact-detail-bloc">
								<div className="label">Address:</div>
								<div className="txt-cont">
									<h3>{customer?.address}</h3>
								</div>
							</div>

							<div className="contact-detail-bloc">
								<div className="label">Business Name:</div>
								<div className="txt-cont">
									<h3>{customer?.business_name}</h3>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* <div className="mb-3 tw-grid tw-gap-5 md:tw-grid-cols-[1fr,2fr]">
					<div className="wallet">
						<div className="mb-2 section-title">
							<h3>Wallet Balance</h3>
						</div>

						<span className="wallet_balance" id="wallet_balance">
							{renderCurrency(customerData?.wallet_balance)}
						</span>
					</div>

					<div className="virtual-account">
						<div className="mb-2 section-title">
							<h3>Virtual Account</h3>
						</div>
						<div>
							{customerData?.virtual_account ? (
								<div className="tw-grid tw-gap-5 tw-grid-cols-[repeat(auto-fill,minmax(150px,1fr))]">
									<div className="">
										<p className="tw-font-semibold tw-text-[22px]">{customerData?.virtual_account?.account_number}</p>
										<p className="tw-font-semibold tw-text-[17px]">{customerData?.virtual_account?.account_name}</p>
										<p className="tw-text-[14px]">{customerData?.virtual_account?.bank_name}</p>
									</div>
								</div>
							) : (
								<CustomConfirmDialog
									trigger={
										<Button className="tw-w-full" variant="secondary">
											Generate Virtual Account
										</Button>
									}
									mutateFn={() => mutate({ uuid: customer?.uuid, bvn: customer?.bvn })}
								/>
							)}
						</div>
					</div>
				</div> */}

				{user?.organization_type_name === "Schools" && (
					<div className="mb-3 trans_details">
						<div className="mb-2 section-title">
							<h3>{getCustomizationValue(user, "sub_account_label")}</h3>
						</div>

						<CustomDataTable
							columns={columns}
							data={customer?.sub_accounts}
							pagination={false}
							containerClassName="!tw-shadow-none"
							emptyStateMessage={cn(
								"This ",
								getCustomizationValue(user, "customer_label", true).toLowerCase(),
								"has no linked ",
								getCustomizationValue(user, "sub_account_label").toLowerCase(),
							)}
							customStyles={{
								headRow: {
									style: {
										paddingInline: "0",
									},
								},
								rows: {
									style: {
										paddingInline: "0",
									},
								},
							}}
						/>
					</div>
				)}

				<div className="mb-3 trans_details">
					<div className="mb-2 section-title">
						<h3>Transaction Details</h3>
					</div>

					<div className="tw-grid tw-grid-cols-[repeat(auto-fit,minmax(200px,1fr))] sm:tw-grid-cols-[repeat(auto-fit,minmax(350px,1fr))] tw-gap-y-5 tw-gap-x-6">
						<div className="trans_itm">
							<p>Total {getCustomizationValue(user, "invoice_label", true)} Value:</p>
							<p className="medium_bold_txt">{renderCurrency(customerData?.total_invoice_value)}</p>
						</div>

						<div className="trans_itm">
							<p>{getCustomizationValue(user, "invoice_label", true)} Issued:</p>
							<p className="medium_bold_txt">{customerData?.total_invoice_count}</p>
						</div>

						<div className="trans_itm">
							<p>Total Payment Made:</p>
							<p className="medium_bold_txt">{renderCurrency(customerData?.total_payments_received)}</p>
						</div>

						<div className="trans_itm">
							<p>Total Debt:</p>
							<p className="medium_bold_txt">{renderCurrency(customerData?.total_owed)}</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CustomerDetail;
